import React, { useEffect, useMemo, useState } from "react";

import { AlertTriangle, X } from "lucide-react";
import toast from "react-hot-toast";

import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";
import { Checkbox } from "@/components/ui/Checkbox";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

import { ComplianceService } from "@/services/complianceService";

/**
 * Reject the CANDIDATE, with a reason per failing document (Phase 7C).
 *
 * Replaces per-document rejection. The distinction: a problem with a *document*
 * is a re-request ("fix this and resend"); a problem with the *candidate* — a
 * record that disqualifies them — is a rejection. Rejecting a document modelled
 * the wrong thing and left the case in limbo with no way to conclude it.
 *
 * The officer selects one or more documents and gives a reason for each. Both
 * are compulsory: the server refuses an empty list or a blank reason, so this
 * modal enforces the same rule client-side rather than letting the user compose
 * a rejection the API will reject. That mirrors re-request, whose description
 * has always been `min_length=1` — the laxness of the old optional reject reason
 * produced real REJECTED rows with no recorded grounds.
 *
 * Deliberately not a generic confirm dialog: a rejection ends the case and can
 * move the candidate's recruitment applications, so the consequences are spelled
 * out before the button is live.
 */
const RejectCandidateModal = ({ open, onClose, complianceCandidateId, candidateName, documents = [], onRejected }) => {
  // { [documentId]: reasonText } — presence of a key means "selected".
  // One map rather than a Set plus a text map, so a document cannot be
  // selected with its reason silently detached.
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  // The post-rejection status lookup that used to run here went with Phase S1:
  // a rejection moves no applications, so there is no destination to describe.
  useEffect(() => {
    if (!open) {
      setSelected({});
      setSaving(false);
      setTouched(false);
    }
  }, [open]);

  const toggle = (docId) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (docId in next) delete next[docId];
      else next[docId] = "";
      return next;
    });
  };

  const setReason = (docId, text) => setSelected((prev) => ({ ...prev, [docId]: text }));

  const selectedIds = useMemo(() => Object.keys(selected), [selected]);
  const missingReasons = useMemo(() => selectedIds.filter((id) => !(selected[id] ?? "").trim()), [selectedIds, selected]);
  const canSubmit = selectedIds.length > 0 && missingReasons.length === 0;

  const handleReject = async () => {
    setTouched(true);
    if (!canSubmit) return;
    setSaving(true);
    try {
      const res = await ComplianceService.rejectCandidate(
        complianceCandidateId,
        selectedIds.map((id) => ({
          documentId: id,
          reason: selected[id].trim(),
        })),
      );
      // `movedApplicationIds` is a deprecated stub, always empty since Phase S1
      // — kept on the response for one release only, so nothing branches on it.
      toast.success("Candidate rejected");
      onRejected?.(res);
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error(apiErrorMessageFormat(e, "Failed to reject candidate"));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-[560px] max-h-[85vh] flex flex-col rounded-2xl bg-background-primary border border-border-primary shadow-xl">
        <div className="flex items-start justify-between gap-3 px-5 py-3 border-b border-border-primary shrink-0">
          <div>
            <h2 className="text-[15px] font-semibold text-text-primary">Reject Candidate</h2>
            <p className="text-[12px] text-text-primary/70 mt-0.5">{candidateName || "This candidate"} will be marked as rejected by compliance.</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="w-7 h-7 rounded-full bg-background-icon/40 shrink-0" aria-label="Close">
            <X size={16} strokeWidth={3} />
          </Button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="rounded-xl border border-destructive/20 bg-destructive/10 p-3 flex gap-2.5">
            <AlertTriangle size={16} className="text-destructive mt-0.5 shrink-0" />
            <div className="text-[11.5px] text-text-primary/80">
              <p className="font-medium text-destructive">This ends the compliance case.</p>
              {/* Phase S1 (Q-PostReject): a rejection never moves an application
                  now, so this states one outcome rather than branching on the
                  configured post-rejection status. That setting is still readable
                  until S3 drops the column, and promising a move the route no
                  longer performs is the worse of the two wrong screens. */}
              <p className="mt-0.5">
                Applications keep their current status — the recruiter decides where the candidate goes.
              </p>
            </div>
          </div>

          <div>
            <p className="text-[12px] text-text-primary/80 mb-1.5">Select the documents that fail compliance, and say why. A reason is required for each.</p>

            {documents.length === 0 ? (
              <p className="text-[11.5px] text-text-primary/50 py-3">This candidate has no documents to reject.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {documents.map((doc) => {
                  const isSelected = doc.id in selected;
                  const isMissing = touched && isSelected && !(selected[doc.id] ?? "").trim();
                  return (
                    <div
                      key={doc.id}
                      className={`rounded-xl border p-3 ${isSelected ? "border-destructive/40 bg-destructive/5" : "border-border-primary bg-background-form/30"}`}
                    >
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <Checkbox checked={isSelected} onCheckedChange={() => toggle(doc.id)} className="mt-0.5" />
                        <span className="text-[12.5px] text-text-primary">
                          {doc.name || "Document"}
                          {doc.status ? <span className="ml-1.5 text-[10.5px] text-text-primary/50">({doc.status.replace(/_/g, " ")})</span> : null}
                        </span>
                      </label>

                      {isSelected && (
                        <div className="mt-2 pl-6">
                          <Textarea
                            rows={2}
                            value={selected[doc.id]}
                            onChange={(e) => setReason(doc.id, e.target.value)}
                            placeholder="Why does this document fail compliance?"
                            className={`bg-background-form/50 px-2 py-1.5 text-[12px] resize-none ${isMissing ? "border-destructive" : ""}`}
                          />
                          {isMissing && <p className="text-[10.5px] text-destructive mt-0.5">A reason is required.</p>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {touched && selectedIds.length === 0 && <p className="text-[11.5px] text-destructive">Select at least one document and give a reason.</p>}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border-primary shrink-0">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleReject}
            // Not disabled on invalid input: pressing it is how the
            // officer discovers WHICH reason is missing, via the
            // per-field errors above. Only a real in-flight request
            // disables it.
            disabled={saving || documents.length === 0}
          >
            {saving ? "Rejecting…" : "Reject Candidate"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RejectCandidateModal;
